/**
 * DeathPopup - Popup que aparece cuando el jugador muere
 *
 * Muestra opciones para reiniciar el nivel o salir a la selección de nivel.
 * Usa Rex UI para los botones con estilo consistente.
 */

import { Scene, GameObjects } from 'phaser';
import * as Phaser from 'phaser';
import UIPlugin from 'phaser3-rex-plugins/templates/ui/ui-plugin.js';
import { audioManager } from '../core/CoreManagerAudio';
import { LayerDepths } from '../constants/enums';
import { t } from '../i18n/Localization';

export interface DeathPopupConfig {
  scene: Scene;
  onRestart?: () => void;
  onExit?: () => void;
}

export class DeathPopup extends GameObjects.Container {
  private rexUI: UIPlugin;
  private overlay: GameObjects.Rectangle;
  private panel: GameObjects.Container;
  private isOpen: boolean = false;
  private prevInputPollRate: number | null = null;
  private prevTopOnly: boolean | null = null;
  private pointerMoveHandler?: (pointer: Phaser.Input.Pointer) => void;
  private pointerDownHandler?: (pointer: Phaser.Input.Pointer) => void;
  private pointerUpHandler?: (pointer: Phaser.Input.Pointer) => void;
  private buttonStates: Array<{
    bg: GameObjects.GameObject & { getBounds: () => Phaser.Geom.Rectangle; setFillStyle: (color: number) => unknown; setScale: (scale: number) => unknown };
    label: GameObjects.Text;
    onClick: () => void;
    normalColor: number;
    hoverColor: number;
    downColor: number;
    isPressed: boolean;
    isHovered: boolean;
  }> = [];
  private onRestart: (() => void) | undefined;
  private onExit: (() => void) | undefined;

  private static readonly PANEL_WIDTH = 380;
  private static readonly PANEL_HEIGHT = 220;

  constructor(config: DeathPopupConfig) {
    const { width, height } = config.scene.scale;
    super(config.scene, width / 2, height / 2);

    this.rexUI = (config.scene as any).rexUI as UIPlugin;
    this.onRestart = config.onRestart;
    this.onExit = config.onExit;

    // Dark overlay covering entire screen and blocking input behind popup
    this.overlay = config.scene.add.rectangle(0, 0, width, height, 0x000000, 0.8)
      .setOrigin(0.5, 0.5)
      .setDepth(0)
      .setInteractive();
    this.overlay.on('pointerdown', () => {
      // Consume clicks behind the popup
    });
    this.add(this.overlay);

    // Create main panel container
    this.panel = this.createPanel(config.scene);
    this.panel.setDepth(1);
    this.add(this.panel);

    // Initially hidden
    this.setVisible(false);
    this.setAlpha(0);
    this.setScrollFactor(0);
    this.setDepth(LayerDepths.DeathPopup);

    config.scene.add.existing(this);
  }

  private playButtonSound(type: 'hover' | 'click'): void {
    audioManager.playUISound(type, this.scene);
  }


  private createPanel(scene: Scene): GameObjects.Container {
    const COLOR_PRIMARY = 0x2a1a1a; // Dark red-ish
    const COLOR_ACCENT = 0xcc4444;  // Red accent
    const COLOR_GREEN = 0x4a9c6d;
    const COLOR_RED = 0x8b3a3a;

    const panel = scene.add.container(0, 0);

    // Background panel - created manually (Rex UI has rendering issues)
    const bg = this.rexUI.add.roundRectangle(0, 0, DeathPopup.PANEL_WIDTH, DeathPopup.PANEL_HEIGHT, 16, COLOR_PRIMARY)
      .setStrokeStyle(3, COLOR_ACCENT);
    panel.add(bg);

    // Title "GAME OVER"
    const title = scene.add.text(0, -65, t('death.title'), {
      fontFamily: 'Arial Black',
      fontSize: '42px',
      color: '#ff4444'
    }).setOrigin(0.5);
    panel.add(title);

    // Subtitle
    const subtitle = scene.add.text(0, -15, t('death.subtitle'), {
      fontFamily: 'Arial',
      fontSize: '18px',
      color: '#aaaaaa'
    }).setOrigin(0.5);
    panel.add(subtitle);

    // Separator
    const separator = scene.add.rectangle(0, 20, DeathPopup.PANEL_WIDTH - 60, 2, COLOR_ACCENT);
    panel.add(separator);

    // Buttons
    const restartBtn = this.createButton(scene, t('death.restart'), COLOR_GREEN, -80, 70, () => {
      this.playButtonSound('click');
      audioManager.stopMusic();
      audioManager.playMusicForScene('MainMenu');
      if (this.onRestart) this.onRestart();
    });
    panel.add(restartBtn);

    const exitBtn = this.createButton(scene, t('death.exit'), COLOR_RED, 80, 70, () => {
      this.playButtonSound('click');
      audioManager.stopMusic();
      audioManager.playMusicForScene('MainMenu');
      if (this.onExit) this.onExit();
    });
    panel.add(exitBtn);

    panel.setSize(DeathPopup.PANEL_WIDTH, DeathPopup.PANEL_HEIGHT);
    panel.setScrollFactor(0);

    return panel;
  }

private createButton(
  scene: Scene,
  text: string,
  bgColor: number,
  x: number,
  y: number,
  onClick: () => void
): GameObjects.Container {

  const container = scene.add.container(x, y);

  const bg = this.rexUI.add.roundRectangle(0, 0, 140, 50, 12, bgColor)
    .setStrokeStyle(2, 0xffffff)
    .setOrigin(0.5);

  const label = scene.add.text(0, 0, text, {
    fontFamily: 'Arial Black',
    fontSize: '22px',
    color: '#ffffff'
  }).setOrigin(0.5);

  container.add([bg, label]);

  // 🔥 FIX REAL: usar tamaño dinámico del sprite (NO hardcode)
  const width = bg.displayWidth;
  const height = bg.displayHeight;

  container.setSize(width, height);
  bg.setInteractive({ useHandCursor: true });

  container.setScrollFactor(0);

  const normalColor = bgColor;
  const hoverColor = Phaser.Display.Color.ValueToColor(bgColor).lighten(20).color;
  const downColor = Phaser.Display.Color.ValueToColor(bgColor).darken(20).color;

  let isPressed = false;

  bg.on('pointerover', () => {
    if (isPressed) return;
    bg.setFillStyle(hoverColor);
    bg.setScale(1.05);
    label.setScale(1.05);
    this.playButtonSound('hover');
  });

  bg.on('pointerout', () => {
    if (isPressed) return;
    bg.setFillStyle(normalColor);
    bg.setScale(1);
    label.setScale(1);
  });

  bg.on('pointerdown', () => {
    isPressed = true;
    bg.setFillStyle(downColor);
    bg.setScale(0.95);
    label.setScale(0.95);
  });

  bg.on('pointerup', () => {
    if (!isPressed) return;
    isPressed = false;
    bg.setFillStyle(hoverColor);
    bg.setScale(1.05);
    label.setScale(1.05);
    this.playButtonSound('click');
    onClick();
  });

  bg.on('pointerupoutside', () => {
    if (!isPressed) return;
    isPressed = false;
    bg.setFillStyle(normalColor);
    bg.setScale(1);
    label.setScale(1);
  });

  bg.on('pointermove', (pointer: Phaser.Input.Pointer) => {
    if (!isPressed) return;
    const isInBounds = bg.getBounds().contains(pointer.x, pointer.y);
    if (!isInBounds) {
      isPressed = false;
      bg.setFillStyle(normalColor);
      bg.setScale(1);
      label.setScale(1);
    }
  });

  this.buttonStates.push({
    bg: bg as GameObjects.GameObject & { getBounds: () => Phaser.Geom.Rectangle; setFillStyle: (color: number) => unknown; setScale: (scale: number) => unknown },
    label,
    onClick,
    normalColor,
    hoverColor,
    downColor,
    isPressed: false,
    isHovered: false,
  });

  return container;
}

  private applyButtonVisual(
    btn: {
      bg: GameObjects.GameObject & { setFillStyle: (color: number) => unknown; setScale: (scale: number) => unknown };
      label: GameObjects.Text;
      normalColor: number;
      hoverColor: number;
      downColor: number;
      isPressed: boolean;
      isHovered: boolean;
    }
  ): void {
    if (btn.isPressed) {
      btn.bg.setFillStyle(btn.downColor);
      btn.bg.setScale(0.95);
      btn.label.setScale(0.95);
      return;
    }
    if (btn.isHovered) {
      btn.bg.setFillStyle(btn.hoverColor);
      btn.bg.setScale(1.05);
      btn.label.setScale(1.05);
      return;
    }
    btn.bg.setFillStyle(btn.normalColor);
    btn.bg.setScale(1);
    btn.label.setScale(1);
  }

  private bindPointerFallback(): void {
    this.unbindPointerFallback();

    this.pointerMoveHandler = (pointer: Phaser.Input.Pointer) => {
      for (const btn of this.buttonStates) {
        const inside = btn.bg.getBounds().contains(pointer.x, pointer.y);
        if (inside !== btn.isHovered) {
          btn.isHovered = inside;
          if (inside) this.playButtonSound('hover');
        }
        if (btn.isPressed && !inside) {
          btn.isPressed = false;
        }
        this.applyButtonVisual(btn);
      }
    };

    this.pointerDownHandler = (pointer: Phaser.Input.Pointer) => {
      for (const btn of this.buttonStates) {
        const inside = btn.bg.getBounds().contains(pointer.x, pointer.y);
        btn.isPressed = inside;
        btn.isHovered = inside;
        this.applyButtonVisual(btn);
      }
    };

    this.pointerUpHandler = (pointer: Phaser.Input.Pointer) => {
      for (const btn of this.buttonStates) {
        const inside = btn.bg.getBounds().contains(pointer.x, pointer.y);
        const shouldClick = btn.isPressed && inside;
        btn.isPressed = false;
        btn.isHovered = inside;
        this.applyButtonVisual(btn);
        if (shouldClick) {
          this.playButtonSound('click');
          btn.onClick();
        }
      }
    };

    this.scene.input.on('pointermove', this.pointerMoveHandler);
    this.scene.input.on('pointerdown', this.pointerDownHandler);
    this.scene.input.on('pointerup', this.pointerUpHandler);
  }

  private unbindPointerFallback(): void {
    if (this.pointerMoveHandler) {
      this.scene.input.off('pointermove', this.pointerMoveHandler);
      this.pointerMoveHandler = undefined;
    }
    if (this.pointerDownHandler) {
      this.scene.input.off('pointerdown', this.pointerDownHandler);
      this.pointerDownHandler = undefined;
    }
    if (this.pointerUpHandler) {
      this.scene.input.off('pointerup', this.pointerUpHandler);
      this.pointerUpHandler = undefined;
    }
    for (const btn of this.buttonStates) {
      btn.isPressed = false;
      btn.isHovered = false;
      this.applyButtonVisual(btn);
    }
  }

  /**
   * Open the death popup
   */
  open(): void {
    if (this.isOpen) return;
    this.isOpen = true;

    audioManager.stopMusic();
    audioManager.playMusicForScene('Death');

    // Ensure hover/out works immediately even if the popup appears under a stationary cursor.
    if (this.prevInputPollRate === null) {
      this.prevInputPollRate = this.scene.input.pollRate;
      this.scene.input.setPollAlways();
    }
    if (this.prevTopOnly === null) {
      this.prevTopOnly = this.scene.input.topOnly;
      this.scene.input.setTopOnly(true);
    }
    if (this.overlay.input && this.overlay.input.enabled) {
      this.overlay.disableInteractive();
    }
    this.bindPointerFallback();

    this.setVisible(true);

    // Animate in
    this.scene.tweens.add({
      targets: this,
      alpha: 1,
      duration: 300,
      ease: 'Power2'
    });

    this.scene.tweens.add({
      targets: this.panel,
      scaleX: { from: 0.5, to: 1 },
      scaleY: { from: 0.5, to: 1 },
      duration: 400,
      ease: 'Back.easeOut'
    });
  }

  /**
   * Close the death popup
   */
  close(): void {
    if (!this.isOpen) return;
    this.isOpen = false;

    // Restore previous input polling rate
    if (this.prevInputPollRate !== null) {
      const prev = this.prevInputPollRate;
      this.prevInputPollRate = null;
      if (prev === -1) {
        this.scene.input.setPollOnMove();
      } else {
        this.scene.input.setPollRate(prev);
      }
    }
    if (this.prevTopOnly !== null) {
      this.scene.input.setTopOnly(this.prevTopOnly);
      this.prevTopOnly = null;
    }
    if (!this.overlay.input || !this.overlay.input.enabled) {
      this.overlay.setInteractive();
    }
    this.unbindPointerFallback();

    // Animate out
    this.scene.tweens.add({
      targets: this,
      alpha: 0,
      duration: 150,
      ease: 'Power2',
      onComplete: () => {
        this.setVisible(false);
      }
    });
  }

  /**
   * Check if popup is open
   */
  getIsOpen(): boolean {
    return this.isOpen;
  }
}
